import { useEffect } from 'react';
import { useErrorReporting } from './useErrorReporting';

export const useGlobalErrorHandler = () => {
  const { reportError } = useErrorReporting();

  useEffect(() => {
    // Handle unhandled promise rejections
    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const message = event.reason?.message || String(event.reason);
      
      // Ignore AbortController signals - these are normal cleanup when components unmount
      if (
        message.includes('signal is aborted') ||
        message.includes('aborted') ||
        message.includes('The operation was aborted') ||
        event.reason?.name === 'AbortError'
      ) {
        console.log('[GLOBAL_ERROR] Ignoring abort signal (normal cleanup)');
        return;
      }
      
      console.error('[GLOBAL_ERROR] Unhandled promise rejection:', event.reason);
      reportError(
        `Unhandled Promise Rejection: ${message}`,
        event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
        {
          type: 'unhandledRejection',
          path: window.location.pathname,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }
      );
    };

    // Handle global JavaScript errors
    const handleError = (event: ErrorEvent) => {
      console.error('[GLOBAL_ERROR] JavaScript error:', event.error);
      reportError(
        `JavaScript Error: ${event.message}`,
        event.error || new Error(event.message),
        {
          type: 'javascript',
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          path: window.location.pathname,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }
      );
    };

    // Add event listeners
    window.addEventListener('unhandledrejection', handleUnhandledRejection);
    window.addEventListener('error', handleError);

    // Cleanup
    return () => {
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
      window.removeEventListener('error', handleError);
    };
  }, [reportError]);
};